// Sprint 17 A9: Lead Scoring Model Builder — criteria + weight sliders + preview
import { useEffect, useState } from "react";
import {
  Sliders, Plus, Trash2, GripVertical, Save,
  Mail, MousePointerClick, Building2, Users,
  ToggleLeft, ToggleRight, Brain, AlertCircle,
  Target, GitBranch, ListChecks,
} from "lucide-react";
import {
  useCrmProgramFactCatalog,
  useCrmProgramConfig,
  usePreviewCrmProgramConfig,
  useReviewCrmProgramConfig,
  useSaveCrmProgramConfig,
  useScoringModel,
  useSaveScoringModel,
} from "@/hooks/use-crm-scoring";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import {
  normalizeCrmProgramPreviewFacts,
  validateCrmProgramPreviewBoundary,
  validateCrmProgramPreviewEvidenceContract,
} from "@/lib/crm-preview-fact-safety";
import type {
  CrmProgramConfig,
  CrmProgramConfigPayload,
  CrmProgramFactCatalog,
  CrmProgramIntentRule,
  CrmProgramPolicyPreview,
  CrmProgramPolicyReview,
  CrmProgramScoreBand,
  ScoringCriterion,
  ScoringModel,
} from "@/hooks/use-crm-scoring";

const CATEGORY_ICONS: Record<string, { Icon: typeof Mail; color: string; label: string }> = {
  engagement: { Icon: Mail, color: "text-[var(--color-primary)]", label: "Engagement" },
  behavioral: { Icon: MousePointerClick, color: "text-[var(--color-accent-action)]", label: "Behavioral" },
  demographic: { Icon: Users, color: "text-[var(--color-success)]", label: "Demographic" },
  firmographic: { Icon: Building2, color: "text-[var(--color-ai)]", label: "Firmographic" },
};

const DEFAULT_SCORING_CRITERIA: ScoringCriterion[] = [
  {
    id: "email-engagement",
    name: "Email engagement",
    category: "engagement",
    weight: 25,
    operator: "exists",
    points: 25,
    enabled: true,
  },
  {
    id: "form-submissions",
    name: "Form submissions",
    category: "behavioral",
    weight: 30,
    operator: "exists",
    points: 30,
    enabled: true,
  },
  {
    id: "webinar-attendance",
    name: "Webinar attendance",
    category: "engagement",
    weight: 25,
    operator: "exists",
    points: 25,
    enabled: true,
  },
  {
    id: "deal-progression",
    name: "Deal progression",
    category: "behavioral",
    weight: 20,
    operator: "exists",
    points: 20,
    enabled: true,
  },
];

const DEFAULT_SCORING_MODEL: ScoringModel = {
  id: "draft",
  name: "Lead Score",
  description: "Default tenant lead scoring model",
  criteria: DEFAULT_SCORING_CRITERIA,
  maxScore: 100,
  hotThreshold: 80,
  warmThreshold: 50,
  isActive: true,
  updatedAt: "",
};

export default function ScoringSettingsPage() {
  const { data: model, isLoading, error, refetch } = useScoringModel();
  const {
    data: programConfig,
    isLoading: isProgramConfigLoading,
    error: programConfigError,
    refetch: refetchProgramConfig,
  } = useCrmProgramConfig();
  const {
    data: factCatalog,
    isLoading: isFactCatalogLoading,
    error: factCatalogError,
    refetch: refetchFactCatalog,
  } = useCrmProgramFactCatalog();
  const { mutate: saveModel, isLoading: isSaving } = useSaveScoringModel();
  const {
    mutate: saveProgramConfig,
    isLoading: isSavingProgramConfig,
    error: saveProgramConfigError,
  } = useSaveCrmProgramConfig();
  const [localCriteria, setLocalCriteria] = useState<ScoringCriterion[] | null>(null);
  const [hotThreshold, setHotThreshold] = useState<number | null>(null);
  const [warmThreshold, setWarmThreshold] = useState<number | null>(null);

  // Use local state for edits, fallback to model data
  const draftModel: ScoringModel = model
    ? { ...DEFAULT_SCORING_MODEL, ...model, criteria: model.criteria ?? DEFAULT_SCORING_CRITERIA }
    : DEFAULT_SCORING_MODEL;
  const criteria = localCriteria ?? draftModel.criteria;
  const hot = hotThreshold ?? draftModel.hotThreshold;
  const warm = warmThreshold ?? draftModel.warmThreshold;

  const totalWeight = criteria.filter((c) => c.enabled).reduce((sum, c) => sum + c.weight, 0);
  const enabledCriteriaCount = criteria.filter((criterion) => criterion.enabled).length;
  const thresholdInvalid = warm >= hot;
  const canSave = criteria.length > 0 && enabledCriteriaCount > 0 && !thresholdInvalid && !isSaving;
  const coldWidth = Math.min(Math.max(warm, 0), 100);
  const warmWidth = Math.min(Math.max(hot - warm, 0), 100);
  const hotWidth = Math.min(Math.max(100 - hot, 0), 100);

  const handleWeightChange = (id: string, weight: number) => {
    setLocalCriteria(
      criteria.map((c) => c.id === id ? { ...c, weight, points: weight } : c),
    );
  };

  const handleToggle = (id: string) => {
    setLocalCriteria(
      criteria.map((c) => c.id === id ? { ...c, enabled: !c.enabled } : c),
    );
  };

  const handleRemove = (id: string) => {
    setLocalCriteria(criteria.filter((c) => c.id !== id));
  };

  const handleAddCriterion = () => {
    const nextIndex = criteria.length + 1;
    setLocalCriteria([
      ...criteria,
      {
        id: `custom-${nextIndex}`,
        name: `Custom signal ${nextIndex}`,
        category: "behavioral",
        weight: 10,
        operator: "exists",
        points: 10,
        enabled: true,
      },
    ]);
  };

  const handleSave = async () => {
    await saveModel({
      ...draftModel,
      criteria,
      hotThreshold: hot,
      warmThreshold: warm,
    });
  };

  if (isLoading) return <ScoringSettingsSkeleton />;

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          <AlertCircle className="h-4 w-4 inline mr-1" />
          {error}
          <button onClick={refetch} className="ml-2 underline">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-foreground)]">Lead Scoring</h1>
          <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
            Configure how contacts are scored. Higher scores = hotter leads.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={!canSave}
          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] hover:bg-[var(--color-primary-hover)] disabled:opacity-50 transition-colors"
        >
          {isSaving ? <Skeleton className="h-4 w-4 bg-white/50" /> : <Save className="h-4 w-4" />}
          {isSaving ? <Skeleton className="h-4 w-16 bg-white/50" /> : "Save Model"}
        </button>
      </div>

      {/* Thresholds */}
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4">
        <h2 className="text-sm font-semibold text-[var(--color-foreground)] mb-3 flex items-center gap-2">
          <Sliders className="h-4 w-4 text-[var(--color-primary)]" />
          Score Thresholds
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <ThresholdSlider
            label="Hot Lead"
            value={hot}
            onChange={(v) => setHotThreshold(v)}
            color="var(--color-destructive)"
            max={100}
          />
          <ThresholdSlider
            label="Warm Lead"
            value={warm}
            onChange={(v) => setWarmThreshold(v)}
            color="var(--color-warning)"
            max={100}
          />
        </div>
        {/* Preview bar */}
        <div className="mt-3 flex h-3 rounded-full overflow-hidden bg-[var(--color-muted)]">
          <div style={{ width: `${coldWidth}%` }} className="bg-[var(--color-muted-foreground)]/20" />
          <div style={{ width: `${warmWidth}%` }} className="bg-[var(--color-warning)]/40" />
          <div style={{ width: `${hotWidth}%` }} className="bg-[var(--color-destructive)]/40" />
        </div>
        <div className="flex justify-between text-[10px] text-[var(--color-muted-foreground)] mt-1">
          <span>Cold (0-{warm - 1})</span>
          <span>Warm ({warm}-{hot - 1})</span>
          <span>Hot ({hot}-100)</span>
        </div>
        {thresholdInvalid ? (
          <p className="mt-2 text-xs text-[var(--color-destructive)]">
            Warm threshold must stay below the hot threshold before this model can be saved.
          </p>
        ) : null}
      </div>

      {/* Weight summary */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-[var(--color-muted-foreground)]">Total weight:</span>
        <span className={`font-bold ${totalWeight > 100 ? "text-[var(--color-destructive)]" : "text-[var(--color-foreground)]"}`}>
          {totalWeight}
        </span>
        {totalWeight > 100 && (
          <span className="text-xs text-[var(--color-muted-foreground)]">(normalized by the scoring engine)</span>
        )}
      </div>

      {/* Criteria list */}
      <div className="space-y-2">
        {criteria.map((criterion) => (
          <CriterionRow
            key={criterion.id}
            criterion={criterion}
            onWeightChange={(w) => handleWeightChange(criterion.id, w)}
            onToggle={() => handleToggle(criterion.id)}
            onRemove={() => handleRemove(criterion.id)}
          />
        ))}
      </div>

      {/* Add criterion button */}
      <button
        type="button"
        onClick={handleAddCriterion}
        className="inline-flex items-center gap-1.5 text-sm text-[var(--color-primary)] hover:text-[var(--color-primary-hover)]"
      >
        <Plus className="h-4 w-4" /> Add scoring criterion
      </button>

      <CrmProgramPolicyPanel
        config={programConfig}
        factCatalog={factCatalog}
        isLoading={isProgramConfigLoading}
        isFactCatalogLoading={isFactCatalogLoading}
        error={programConfigError}
        factCatalogError={factCatalogError}
        saveError={saveProgramConfigError}
        isSaving={isSavingProgramConfig}
        onRetry={() => {
          refetchProgramConfig();
          refetchFactCatalog();
        }}
        onSave={async (payload) => {
          const result = await saveProgramConfig(payload);
          if (result?.data) {
            refetchProgramConfig();
            refetchFactCatalog();
            return true;
          }
          return false;
        }}
      />

      {/* Model readiness */}
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/30 p-4">
        <div className="flex items-center gap-2 mb-2">
          <ListChecks className="h-4 w-4 text-[var(--color-primary)]" />
          <span className="text-xs font-semibold text-[var(--color-foreground)]">Model readiness</span>
        </div>
        <div className="space-y-1 text-xs text-[var(--color-foreground)]">
          <p>{enabledCriteriaCount} enabled criteria across {criteria.length} total criteria.</p>
          <p>Total enabled weight is {totalWeight}; weights are normalized by the scoring engine at calculation time.</p>
          <p>
            {model
              ? "Editing the tenant's active scoring model."
              : "No active model found; saving creates a tenant scoring model draft."}
          </p>
        </div>
      </div>
    </div>
  );
}

function CrmProgramPolicyPanel({
  config,
  factCatalog,
  isLoading,
  isFactCatalogLoading,
  error,
  factCatalogError,
  saveError,
  isSaving = false,
  onRetry,
  onSave,
}: {
  config: CrmProgramConfig | null;
  factCatalog: CrmProgramFactCatalog | null;
  isLoading: boolean;
  isFactCatalogLoading: boolean;
  error: string | null;
  factCatalogError: string | null;
  saveError?: string | null;
  isSaving?: boolean;
  onRetry: () => void;
  onSave?: (payload: CrmProgramConfigPayload) => Promise<boolean>;
}) {
  const [draftJson, setDraftJson] = useState("");
  const [previewFactsJson, setPreviewFactsJson] = useState("{\n  \"formSubmissions\": 1,\n  \"websiteVisits\": 3,\n  \"icpMatch\": true\n}");
  const [draftError, setDraftError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<CrmProgramPolicyPreview | null>(null);
  const [reviewResult, setReviewResult] = useState<CrmProgramPolicyReview | null>(null);
  const {
    mutate: previewProgramConfig,
    isLoading: isPreviewing,
    error: previewError,
  } = usePreviewCrmProgramConfig();
  const {
    mutate: reviewProgramConfig,
    isLoading: isReviewing,
    error: reviewError,
  } = useReviewCrmProgramConfig();

  useEffect(() => {
    if (!config) return;
    setDraftJson(JSON.stringify(toProgramConfigPayload(config), null, 2));
    setDraftError(null);
    setSaveNotice(null);
    setPreviewResult(null);
    setReviewResult(null);
  }, [config]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <SkeletonCard className="h-28" />
        <div className="grid gap-3 md:grid-cols-2">
          <SkeletonCard className="h-40" />
          <SkeletonCard className="h-40" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
        <AlertCircle className="h-4 w-4 inline mr-1" />
        Failed to load reusable CRM program policy: {error}
        <button onClick={onRetry} className="ml-2 underline">Retry</button>
      </div>
    );
  }

  if (!config) return null;

  const handleSavePolicy = async () => {
    if (!onSave) return;
    setDraftError(null);
    setSaveNotice(null);
    setReviewResult(null);

    let parsed: unknown;
    try {
      parsed = JSON.parse(draftJson);
    } catch {
      setDraftError("Policy JSON must parse before it can be saved.");
      return;
    }

    if (!isObjectRecord(parsed)) {
      setDraftError("Policy JSON must be an object.");
      return;
    }

    const { source: _source, ...payload } = parsed as Record<string, unknown>;
    const validationError = validateProgramPolicyDraft(payload);
    if (validationError) {
      setDraftError(validationError);
      return;
    }
    const saved = await onSave(payload as unknown as CrmProgramConfigPayload);
    setSaveNotice(saved ? "Reusable CRM policy saved for this tenant." : null);
  };

  const handleReviewPolicy = async () => {
    setDraftError(null);
    setSaveNotice(null);
    setReviewResult(null);

    const parsedDraft = parseJsonObject(draftJson, "Policy JSON");
    if (!parsedDraft.ok) {
      setDraftError(parsedDraft.error);
      return;
    }

    const { source: _source, ...payload } = parsedDraft.value;
    const validationError = validateProgramPolicyDraft(payload);
    if (validationError) {
      setDraftError(validationError);
      return;
    }

    const result = await reviewProgramConfig({
      config: payload as unknown as CrmProgramConfigPayload,
    });
    setReviewResult(result?.data ?? null);
  };

  const handlePreviewPolicy = async () => {
    setDraftError(null);
    setSaveNotice(null);
    setPreviewResult(null);

    const parsedDraft = parseJsonObject(draftJson, "Policy JSON");
    if (!parsedDraft.ok) {
      setDraftError(parsedDraft.error);
      return;
    }

    const { source: _source, ...payload } = parsedDraft.value;
    const validationError = validateProgramPolicyDraft(payload);
    if (validationError) {
      setDraftError(validationError);
      return;
    }

    const parsedFacts = parseJsonObject(previewFactsJson, "Preview facts JSON");
    if (!parsedFacts.ok) {
      setDraftError(parsedFacts.error);
      return;
    }

    const safeFacts = normalizeCrmProgramPreviewFacts(parsedFacts.value, "Preview facts JSON");
    if (!safeFacts.ok) {
      setDraftError(safeFacts.error);
      return;
    }

    const result = await previewProgramConfig({
      config: payload as unknown as CrmProgramConfigPayload,
      facts: safeFacts.value,
    });
    const preview = result?.data ?? null;
    const boundaryError = preview
      ? validateCrmProgramPreviewBoundary(preview, "CRM program preview response")
        ?? validateCrmProgramPreviewEvidenceContract(
          preview.previewEvidence,
          "CRM program preview evidence",
          preview.configDigestSha256,
        )
      : null;
    if (boundaryError) {
      setDraftError(boundaryError);
      return;
    }
    setPreviewResult(preview);
  };

  const draftPayload = parseDraftPayloadForControls(draftJson);

  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-[var(--color-foreground)] flex items-center gap-2">
            <Target className="h-4 w-4 text-[var(--color-primary)]" />
            Reusable CRM Program Policy
          </h2>
          <p className="text-xs text-[var(--color-muted-foreground)] mt-1">
            Tenant-owned scoring, intent, task, and projection rules. English Ease runs as configuration on the same Stero policy engine.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wide">
          <span className="rounded-full border border-[var(--color-border)] px-2 py-1 text-[var(--color-muted-foreground)]">
            {config.source}
          </span>
          <span className="rounded-full border border-[var(--color-border)] px-2 py-1 text-[var(--color-muted-foreground)]">
            {config.programVersion}
          </span>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        {config.scoreBands.map((band) => (
          <div key={band.key} className="rounded-lg bg-[var(--color-muted)]/40 p-3">
            <div className="text-xs font-semibold text-[var(--color-foreground)]">{band.label}</div>
            <div className="text-[10px] text-[var(--color-muted-foreground)]">Score {band.minScore}+</div>
          </div>
        ))}
      </div>

      <CrmProgramPolicySafetyStrip config={config} />

      <CrmProgramFactCatalogPanel
        catalog={factCatalog}
        isLoading={isFactCatalogLoading}
        error={factCatalogError}
        onRetry={onRetry}
      />

      <CrmProgramStructuredDraftControls
        payload={draftPayload}
        onUpdate={(updater) => {
          const result = updateDraftJson(draftJson, updater);
          if (!result.ok) {
            setDraftError(result.error);
            return;
          }
          setDraftJson(result.value);
          setDraftError(null);
          setSaveNotice(null);
          setPreviewResult(null);
          setReviewResult(null);
        }}
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
            Score Components
          </h3>
          {config.scoreComponents.map((component) => (
            <div key={component.key} className="rounded-lg border border-[var(--color-border)] p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-medium text-[var(--color-foreground)]">{component.label}</div>
                <div className="text-xs font-mono text-[var(--color-muted-foreground)]">{component.maxScore} pts</div>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {component.signals.map((signal) => (
                  <span key={signal.key} className="rounded-full bg-[var(--color-muted)] px-2 py-1 text-[10px] text-[var(--color-foreground)]">
                    {signal.label} +{signal.points}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)] flex items-center gap-1.5">
            <GitBranch className="h-3.5 w-3.5" />
            Intent Rules
          </h3>
          {config.intentRules.map((rule) => (
            <div key={rule.key} className="rounded-lg border border-[var(--color-border)] p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-[var(--color-foreground)]">{rule.label}</span>
                <span className="rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 text-[10px] text-[var(--color-primary)]">
                  {rule.intentClass}
                </span>
              </div>
              <p className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">{rule.when}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-[var(--color-border)] p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)] flex items-center gap-1.5">
            <ListChecks className="h-3.5 w-3.5" />
            Task Automation
          </h3>
          <div className="mt-3 space-y-2">
            {config.taskRules.map((rule) => (
              <div key={rule.key} className="text-xs text-[var(--color-foreground)]">
                <span className="font-medium">{rule.title}</span>
                <span className="text-[var(--color-muted-foreground)]"> when {rule.triggerIntent}, due in {rule.dueInHours}h</span>
                {(rule.taskType || rule.priority || rule.nextAction || rule.scriptTemplate) && (
                  <p className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">
                    {[rule.taskType, rule.priority, rule.nextAction, rule.scriptTemplate ? "script" : null].filter(Boolean).join(" / ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-[var(--color-border)] p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
            Person Projections
          </h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {config.personProjectionRules.map((rule) => (
              <span key={rule.key} className="rounded-md bg-[var(--color-muted)] px-2 py-1 text-[10px] text-[var(--color-foreground)]">
                {rule.targetField} &lt;- {rule.source}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-[var(--color-border)] p-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
              Draft Review
            </h3>
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              Review the current draft through the API before saving. Findings are advisory here; the save route still performs final validation.
            </p>
          </div>
          <button
            type="button"
            onClick={handleReviewPolicy}
            disabled={isReviewing}
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md border border-[var(--color-border)] px-3 py-2 text-xs font-medium text-[var(--color-foreground)] transition-colors hover:border-[var(--color-primary)]/40 disabled:opacity-50"
          >
            {isReviewing ? <Skeleton className="h-3.5 w-3.5" /> : <ListChecks className="h-3.5 w-3.5" />}
            {isReviewing ? "Reviewing..." : "Review draft"}
          </button>
        </div>
        {reviewError ? (
          <p className="mt-2 text-xs text-[var(--color-destructive)]">{reviewError}</p>
        ) : null}
        {reviewResult ? <CrmProgramReviewResult review={reviewResult} /> : null}
      </div>

      <div className="rounded-lg border border-[var(--color-border)] p-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
              Tenant Policy JSON
            </h3>
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              JSON remains the source of truth for components, gates, tasks, projections, and event contracts. Structured controls above only rewrite scoreBands and intentRules.
            </p>
          </div>
          <button
            type="button"
            onClick={handleSavePolicy}
            disabled={isSaving}
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md bg-[var(--color-foreground)] px-3 py-2 text-xs font-medium text-[var(--color-background)] transition-colors hover:bg-[var(--color-foreground)]/90 disabled:opacity-50"
          >
            {isSaving ? <Skeleton className="h-3.5 w-3.5 bg-white/50" /> : <Save className="h-3.5 w-3.5" />}
            {isSaving ? "Saving..." : "Save reusable policy"}
          </button>
        </div>
        <textarea
          aria-label="CRM program policy JSON"
          value={draftJson}
          onChange={(event) => {
            setDraftJson(event.target.value);
            setDraftError(null);
            setSaveNotice(null);
            setReviewResult(null);
          }}
          spellCheck={false}
          className="mt-3 min-h-64 w-full resize-y rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-3 font-mono text-[11px] leading-relaxed text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/20"
        />
        {(draftError || saveError) && (
          <p className="mt-2 text-xs text-[var(--color-destructive)]">
            {draftError ?? saveError}
          </p>
        )}
        {saveNotice && (
          <p className="mt-2 text-xs text-[var(--color-success)]">
            {saveNotice}
          </p>
        )}
      </div>

      <div className="rounded-lg border border-[var(--color-border)] p-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
              Policy Preview
            </h3>
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              Evaluate the draft against sample facts before saving. The preview returns fact keys, score, intent, and task plans only; it performs no CRM writes.
            </p>
          </div>
          <button
            type="button"
            onClick={handlePreviewPolicy}
            disabled={isPreviewing}
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md border border-[var(--color-border)] px-3 py-2 text-xs font-medium text-[var(--color-foreground)] transition-colors hover:border-[var(--color-primary)]/40 disabled:opacity-50"
          >
            {isPreviewing ? <Skeleton className="h-3.5 w-3.5" /> : <Brain className="h-3.5 w-3.5" />}
            {isPreviewing ? "Previewing..." : "Preview policy"}
          </button>
        </div>
        <textarea
          aria-label="CRM program preview facts JSON"
          value={previewFactsJson}
          onChange={(event) => {
            setPreviewFactsJson(event.target.value);
            setPreviewResult(null);
          }}
          spellCheck={false}
          className="mt-3 min-h-32 w-full resize-y rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-3 font-mono text-[11px] leading-relaxed text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary)]/20"
        />
        {previewError ? (
          <p className="mt-2 text-xs text-[var(--color-destructive)]">{previewError}</p>
        ) : null}
        {previewResult ? <CrmProgramPreviewResult result={previewResult} /> : null}
      </div>
    </section>
  );
}

function toProgramConfigPayload(config: CrmProgramConfig): CrmProgramConfigPayload {
  const { source: _source, ...payload } = config;
  return payload;
}

function CrmProgramFactCatalogPanel({
  catalog,
  isLoading,
  error,
  onRetry,
}: {
  catalog: CrmProgramFactCatalog | null;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  if (isLoading) return <SkeletonCard className="h-36" />;

  if (error) {
    return (
      <div className="rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-3 text-xs text-[var(--color-destructive)]">
        Failed to load policy fact catalog: {error}
        <button type="button" onClick={onRetry} className="ml-2 underline">Retry</button>
      </div>
    );
  }

  if (!catalog) return null;

  return (
    <div data-policy-fact-catalog className="rounded-lg border border-[var(--color-border)] p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
            Saved Policy Fact Catalog
          </h3>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Business signals the saved/default tenant policy expects. Draft edits are checked by Review Draft before save.
            The catalog returns keys and consumers only, not raw lead facts.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">
          <span className="rounded-full border border-[var(--color-border)] px-2 py-1">{catalog.counts.facts} facts</span>
          <span className="rounded-full border border-[var(--color-border)] px-2 py-1">{catalog.counts.scoreSignalUsages} signals</span>
          <span className="rounded-full border border-[var(--color-border)] px-2 py-1">{catalog.counts.intentConditionUsages} intent uses</span>
          <span className="rounded-full border border-[var(--color-border)] px-2 py-1">no raw facts</span>
        </div>
      </div>

      <div className="mt-3 grid gap-2 md:grid-cols-2">
        {catalog.facts.slice(0, 8).map((fact) => (
          <div key={fact.factKey} className="rounded-lg bg-[var(--color-muted)]/30 p-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-mono text-xs font-semibold text-[var(--color-foreground)]">{fact.factKey}</span>
              <span className="text-[10px] text-[var(--color-muted-foreground)]">{fact.usages.length} uses</span>
            </div>
            <p className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">
              {formatFactConsumers(fact)}
            </p>
            <p className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">
              {fact.usages.map((usage) => formatFactUsageKind(usage.kind)).join(", ")}
            </p>
          </div>
        ))}
      </div>
      {catalog.facts.length > 8 ? (
        <p className="mt-2 text-[10px] text-[var(--color-muted-foreground)]">
          {catalog.facts.length - 8} more fact keys are available through the API response.
        </p>
      ) : null}
      <p className="mt-3 break-all rounded-lg bg-[var(--color-background)] p-2 font-mono text-[10px] text-[var(--color-muted-foreground)]">
        digest {catalog.configDigestSha256}
      </p>
    </div>
  );
}

function CrmProgramStructuredDraftControls({
  payload,
  onUpdate,
}: {
  payload: CrmProgramConfigPayload | null;
  onUpdate: (updater: (draft: Record<string, unknown>) => void) => void;
}) {
  const scoreBands = payload?.scoreBands ?? [];
  const intentRules = payload?.intentRules ?? [];

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-lg border border-[var(--color-border)] p-3">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
            Score Bands
          </h3>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Quick-edit labels and thresholds. Keys stay stable for downstream rules.
          </p>
        </div>
        <div className="mt-3 space-y-2">
          {scoreBands.map((band, index) => (
            <div key={`${band.key}-${index}`} className="grid gap-2 rounded-lg bg-[var(--color-muted)]/30 p-2 sm:grid-cols-[minmax(0,1fr)_7rem]">
              <label className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
                {band.key} label
                <input
                  aria-label={`Score band ${band.key} label`}
                  value={band.label}
                  onChange={(event) => onUpdate((draft) => {
                    const bands = normalizeScoreBands(draft.scoreBands);
                    const currentBand = bands[index];
                    if (!currentBand) return;
                    bands[index] = { ...currentBand, label: event.target.value };
                    draft.scoreBands = bands;
                  })}
                  className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1.5 text-xs font-normal normal-case tracking-normal text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                />
              </label>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
                Min score
                <input
                  aria-label={`Score band ${band.key} minimum score`}
                  type="number"
                  value={band.minScore}
                  onChange={(event) => onUpdate((draft) => {
                    const bands = normalizeScoreBands(draft.scoreBands);
                    const currentBand = bands[index];
                    if (!currentBand) return;
                    bands[index] = { ...currentBand, minScore: Number(event.target.value) };
                    draft.scoreBands = bands;
                  })}
                  className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1.5 text-xs font-normal normal-case tracking-normal text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                />
              </label>
            </div>
          ))}
          {scoreBands.length === 0 ? (
            <p className="rounded-lg bg-[var(--color-muted)]/30 p-3 text-xs text-[var(--color-muted-foreground)]">
              Fix policy JSON to edit score bands here.
            </p>
          ) : null}
        </div>
      </div>

      <div className="rounded-lg border border-[var(--color-border)] p-3">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
            Intent Rules
          </h3>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Quick-edit rule labels, classes, matching text, and priority. Conditions remain available in JSON.
          </p>
        </div>
        <div className="mt-3 space-y-2">
          {intentRules.map((rule, index) => (
            <div key={`${rule.key}-${index}`} className="grid gap-2 rounded-lg bg-[var(--color-muted)]/30 p-2 sm:grid-cols-2">
              <label className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
                {rule.key} label
                <input
                  aria-label={`Intent rule ${rule.key} label`}
                  value={rule.label}
                  onChange={(event) => onUpdate((draft) => {
                    const rules = normalizeIntentRules(draft.intentRules);
                    const currentRule = rules[index];
                    if (!currentRule) return;
                    rules[index] = { ...currentRule, label: event.target.value };
                    draft.intentRules = rules;
                  })}
                  className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1.5 text-xs font-normal normal-case tracking-normal text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                />
              </label>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
                Intent class
                <input
                  aria-label={`Intent rule ${rule.key} intent class`}
                  value={rule.intentClass}
                  onChange={(event) => onUpdate((draft) => {
                    const rules = normalizeIntentRules(draft.intentRules);
                    const currentRule = rules[index];
                    if (!currentRule) return;
                    rules[index] = { ...currentRule, intentClass: event.target.value };
                    draft.intentRules = rules;
                  })}
                  className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1.5 text-xs font-normal normal-case tracking-normal text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                />
              </label>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)] sm:col-span-2">
                When
                <input
                  aria-label={`Intent rule ${rule.key} when`}
                  value={rule.when}
                  onChange={(event) => onUpdate((draft) => {
                    const rules = normalizeIntentRules(draft.intentRules);
                    const currentRule = rules[index];
                    if (!currentRule) return;
                    rules[index] = { ...currentRule, when: event.target.value };
                    draft.intentRules = rules;
                  })}
                  className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1.5 text-xs font-normal normal-case tracking-normal text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                />
              </label>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">
                Priority
                <input
                  aria-label={`Intent rule ${rule.key} priority`}
                  type="number"
                  value={rule.priority}
                  onChange={(event) => onUpdate((draft) => {
                    const rules = normalizeIntentRules(draft.intentRules);
                    const currentRule = rules[index];
                    if (!currentRule) return;
                    rules[index] = { ...currentRule, priority: Number(event.target.value) };
                    draft.intentRules = rules;
                  })}
                  className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1.5 text-xs font-normal normal-case tracking-normal text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                />
              </label>
            </div>
          ))}
          {intentRules.length === 0 ? (
            <p className="rounded-lg bg-[var(--color-muted)]/30 p-3 text-xs text-[var(--color-muted-foreground)]">
              Fix policy JSON to edit intent rules here.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CrmProgramPreviewResult({ result }: { result: CrmProgramPolicyPreview }) {
  const qualificationGateResults = result.decision.qualificationGateResults ?? [];
  const matchedQualificationGateKeys = result.previewEvidence.matchedQualificationGateKeys ?? [];
  const matchedQualificationGateReasonCodes = result.previewEvidence.matchedQualificationGateReasonCodes ?? [];

  return (
    <div aria-label="CRM program preview result" className="mt-3 rounded-lg bg-[var(--color-muted)]/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-[var(--color-background)] px-2 py-1 text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">
          Preview result
        </span>
        <span className="text-xs font-semibold text-[var(--color-foreground)]">
          Score {result.decision.score} / {result.decision.scoreBand}
          {result.decision.scoreBeforeQualification !== result.decision.score
            ? ` (pre-gate ${result.decision.scoreBeforeQualification})`
            : ""}
        </span>
        <span className="text-xs font-semibold text-[var(--color-primary)]">
          Intent {result.decision.intentClass}
        </span>
        <span className="text-xs font-semibold text-[var(--color-muted-foreground)]">
          Gate {result.decision.qualificationGate}
        </span>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">Reason codes</p>
          <p className="mt-1 text-xs text-[var(--color-foreground)]">
            {result.decision.reasonCodes.length > 0 ? result.decision.reasonCodes.join(", ") : "No score reasons"}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">Fact keys</p>
          <p className="mt-1 text-xs text-[var(--color-foreground)]">
            {result.factKeys.length > 0 ? result.factKeys.join(", ") : "No facts supplied"}
          </p>
          <p className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">
            Missing: {formatPreviewFactKeys(result.factCoverage.missingConfiguredFactKeys)}
          </p>
          <p className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">
            Unused: {formatPreviewFactKeys(result.factCoverage.unusedSuppliedFactKeys)}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">Qualification gates</p>
          <p className="mt-1 text-xs text-[var(--color-foreground)]">
            {qualificationGateResults.length > 0
              ? qualificationGateResults
                .filter((gate) => gate.matched)
                .map(formatQualificationGatePreview)
                .join(", ") || "No gates matched"
              : "No gates configured"}
          </p>
          <p className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">
            Evidence gates: {matchedQualificationGateKeys.length > 0
              ? matchedQualificationGateKeys.join(", ")
              : "None"}
          </p>
          <p className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">
            Evidence gate reasons: {matchedQualificationGateReasonCodes.length > 0
              ? matchedQualificationGateReasonCodes.join(", ")
              : "None"}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">Task plan</p>
          <p className="mt-1 text-xs text-[var(--color-foreground)]">
            {result.taskRecommendations.length > 0
              ? result.taskRecommendations.map((task) => {
                  const details = [task.taskType ?? task.templateKey, task.nextAction].filter(Boolean).join(" / ");
                  return `${task.title} (${task.dueInHours}h)${details ? ` [${details}]` : ""}`;
                }).join(", ")
              : "No task recommendation"}
          </p>
          <p className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">
            Evidence: {result.previewEvidence.coverageStatus}, digest {result.previewEvidenceDigestSha256}
          </p>
        </div>
      </div>
      <p className="mt-3 text-[10px] text-[var(--color-muted-foreground)]">
        No raw fact values returned. Target writes: {result.targetWritesExecuted ? "executed" : "not executed"}.
      </p>
    </div>
  );
}

function CrmProgramReviewResult({ review }: { review: CrmProgramPolicyReview }) {
  return (
    <div aria-label="CRM program policy review result" className="mt-3 rounded-lg bg-[var(--color-muted)]/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-wide ${reviewStatusClass(review.status)}`}>
          {review.status}
        </span>
        <span className="text-xs text-[var(--color-muted-foreground)]">
          {review.counts.scoreBands} bands, {review.counts.intentRules} intent rules, {review.counts.scoreSignals} signals
        </span>
      </div>
      <div className="mt-3 rounded-lg bg-[var(--color-background)] p-2 text-[10px] text-[var(--color-muted-foreground)]">
        Actions: {review.targetActionRefs.length > 0 ? review.targetActionRefs.join(", ") : "none"}.
        {" "}Target writes: {review.targetWritesExecuted ? "executed" : "not executed"}.
      </div>
      <div className="mt-3 space-y-2">
        {review.findings.length > 0 ? review.findings.map((finding) => (
          <div key={`${finding.code}-${finding.path ?? "policy"}`} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-2 text-xs">
            <p className="font-semibold text-[var(--color-foreground)]">
              {finding.severity}: {finding.code}
            </p>
            <p className="mt-1 text-[var(--color-muted-foreground)]">{finding.message}</p>
            {finding.path ? <p className="mt-1 text-[10px] text-[var(--color-primary)]">{finding.path}</p> : null}
          </div>
        )) : (
          <p className="rounded-lg bg-[var(--color-background)] p-2 text-xs text-[var(--color-muted-foreground)]">
            No policy findings returned by the review route.
          </p>
        )}
      </div>
    </div>
  );
}

function formatPreviewFactKeys(factKeys: string[]): string {
  return factKeys.length > 0 ? factKeys.join(", ") : "None";
}

function formatQualificationGatePreview(
  gate: NonNullable<CrmProgramPolicyPreview["decision"]["qualificationGateResults"]>[number],
): string {
  const cap = gate.appliedCapScore !== null
    ? ` (${gate.effect} ${gate.appliedCapScore})`
    : ` (${gate.effect})`;
  const reason = gate.reasonCode ? ` reason:${gate.reasonCode}` : "";
  return `${gate.key}${cap}${reason}`;
}

function CrmProgramPolicySafetyStrip({ config }: { config: CrmProgramConfig }) {
  const defaultRuleCount = config.intentRules.filter(isDefaultIntentRuleValue).length;
  const unsafeProjectionCount = config.personProjectionRules.filter((rule) => rule.piiSafe !== true).length;
  const taskActionCount = new Set(config.taskRules.map((rule) => rule.actionRef)).size;

  return (
    <div className="grid gap-3 md:grid-cols-3">
      <PolicySafetyCard
        label="Fail-closed default"
        value={defaultRuleCount === 1 ? "1 rule" : `${defaultRuleCount} rules`}
        status={defaultRuleCount === 1 ? "ok" : "blocked"}
        detail="Exactly one default intent catches low-signal contacts."
      />
      <PolicySafetyCard
        label="PII-safe projections"
        value={unsafeProjectionCount === 0 ? `${config.personProjectionRules.length} safe` : `${unsafeProjectionCount} unsafe`}
        status={unsafeProjectionCount === 0 ? "ok" : "blocked"}
        detail="Projected fields must stay tenant-owned and PII-safe."
      />
      <PolicySafetyCard
        label="Task action refs"
        value={`${taskActionCount} configured`}
        status="ok"
        detail="Automation targets stay on versioned CRM action contracts."
      />
    </div>
  );
}

function PolicySafetyCard({
  label,
  value,
  detail,
  status,
}: {
  label: string;
  value: string;
  detail: string;
  status: "ok" | "blocked";
}) {
  return (
    <div className={`rounded-lg border p-3 ${
      status === "ok"
        ? "border-[var(--color-success)]/20 bg-[var(--color-success)]/5"
        : "border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5"
    }`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-muted-foreground)]">{label}</span>
        <span className={`rounded-full px-2 py-0.5 text-[10px] ${
          status === "ok"
            ? "bg-[var(--color-success)]/10 text-[var(--color-success)]"
            : "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]"
        }`}>
          {status === "ok" ? "Ready" : "Blocked"}
        </span>
      </div>
      <div className="mt-2 text-sm font-semibold text-[var(--color-foreground)]">{value}</div>
      <p className="mt-1 text-[10px] leading-4 text-[var(--color-muted-foreground)]">{detail}</p>
    </div>
  );
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

type JsonObjectParseResult =
  | { ok: true; value: Record<string, unknown> }
  | { ok: false; error: string };

function parseJsonObject(text: string, label: string): JsonObjectParseResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: `${label} must parse before it can be used.` };
  }

  if (!isObjectRecord(parsed)) {
    return { ok: false, error: `${label} must be an object.` };
  }

  return { ok: true, value: parsed };
}

function parseDraftPayloadForControls(text: string): CrmProgramConfigPayload | null {
  const parsed = parseJsonObject(text, "Policy JSON");
  if (!parsed.ok) return null;
  const { source: _source, ...payload } = parsed.value;
  if (!Array.isArray(payload.scoreBands) || !Array.isArray(payload.intentRules)) {
    return null;
  }
  return payload as unknown as CrmProgramConfigPayload;
}

function formatFactConsumers(fact: CrmProgramFactCatalog["facts"][number]): string {
  const parts = [
    fact.consumers.scoreComponents.length > 0 ? `components: ${fact.consumers.scoreComponents.join(", ")}` : null,
    fact.consumers.scoreSignals.length > 0 ? `signals: ${fact.consumers.scoreSignals.join(", ")}` : null,
    fact.consumers.intentRules.length > 0 ? `intent: ${fact.consumers.intentRules.join(", ")}` : null,
    fact.consumers.qualificationGates.length > 0 ? `gates: ${fact.consumers.qualificationGates.join(", ")}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" | ") : "No consumers";
}

function formatFactUsageKind(kind: CrmProgramFactCatalog["facts"][number]["usages"][number]["kind"]): string {
  switch (kind) {
    case "score_signal":
      return "score signal";
    case "score_condition":
      return "score condition";
    case "score_point_model":
      return "point model";
    case "intent_condition":
      return "intent condition";
    case "qualification_gate_condition":
      return "gate condition";
  }
}

type DraftJsonUpdateResult =
  | { ok: true; value: string }
  | { ok: false; error: string };

function updateDraftJson(
  text: string,
  updater: (draft: Record<string, unknown>) => void,
): DraftJsonUpdateResult {
  const parsed = parseJsonObject(text, "Policy JSON");
  if (!parsed.ok) return parsed;
  updater(parsed.value);
  return { ok: true, value: JSON.stringify(parsed.value, null, 2) };
}

function normalizeScoreBands(value: unknown): CrmProgramScoreBand[] {
  return Array.isArray(value) ? value as CrmProgramScoreBand[] : [];
}

function normalizeIntentRules(value: unknown): CrmProgramIntentRule[] {
  return Array.isArray(value) ? value as CrmProgramIntentRule[] : [];
}

function validateProgramPolicyDraft(payload: Record<string, unknown>): string | null {
  const intentRules = payload.intentRules;
  if (!Array.isArray(intentRules)) {
    return "Policy must include an intentRules array.";
  }

  const defaultRules = intentRules.filter(isDefaultIntentRuleValue);
  if (defaultRules.length !== 1) {
    return "Policy must include exactly one fail-closed default intent rule.";
  }

  const defaultRule = defaultRules[0];
  if (defaultRuleHasNonDefaultCondition(defaultRule)) {
    return "Default intent rule cannot include a non-default condition.";
  }

  return null;
}

function reviewStatusClass(status: CrmProgramPolicyReview["status"]): string {
  switch (status) {
    case "ready":
      return "bg-[var(--color-success)]/10 text-[var(--color-success)]";
    case "needs_attention":
      return "bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
    case "blocked":
      return "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
}

function isDefaultIntentRuleValue(value: unknown): boolean {
  if (!isObjectRecord(value)) return false;
  const when = typeof value.when === "string" ? value.when.trim().toLowerCase() : "";
  const condition = isObjectRecord(value.condition) ? value.condition : null;
  return when === "default" || condition?.operator === "default";
}

function defaultRuleHasNonDefaultCondition(value: unknown): boolean {
  if (!isObjectRecord(value)) return false;
  const when = typeof value.when === "string" ? value.when.trim().toLowerCase() : "";
  const condition = isObjectRecord(value.condition) ? value.condition : null;
  return when === "default" && condition !== null && condition.operator !== "default";
}

function CriterionRow({ criterion, onWeightChange, onToggle, onRemove }: {
  criterion: ScoringCriterion;
  onWeightChange: (w: number) => void;
  onToggle: () => void;
  onRemove: () => void;
}) {
  const catConfig = CATEGORY_ICONS[criterion.category] ?? { Icon: Mail, color: "text-[var(--color-primary)]", label: "Other" };
  const CatIcon = catConfig.Icon;
  const ToggleIcon = criterion.enabled ? ToggleRight : ToggleLeft;

  return (
    <div className={`stagger-item flex items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3 ${
      !criterion.enabled ? "opacity-50" : ""
    }`}>
      {/* Drag handle */}
      <GripVertical className="h-4 w-4 text-[var(--color-muted-foreground)] cursor-grab flex-shrink-0" />

      {/* Category icon */}
      <CatIcon className={`h-4 w-4 flex-shrink-0 ${catConfig.color}`} />

      {/* Name + category */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--color-foreground)] truncate">{criterion.name}</p>
        <p className="text-[10px] text-[var(--color-muted-foreground)]">{catConfig.label}</p>
      </div>

      {/* Weight slider */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <input
          type="range"
          min={0}
          max={50}
          value={criterion.weight}
          onChange={(e) => onWeightChange(Number(e.target.value))}
          disabled={!criterion.enabled}
          className="w-20 accent-[var(--color-primary)]"
        />
        <span className="text-xs font-mono w-8 text-right text-[var(--color-foreground)]">{criterion.weight}</span>
      </div>

      {/* Toggle */}
      <button onClick={onToggle} className="flex-shrink-0" aria-label={criterion.enabled ? "Disable criterion" : "Enable criterion"}>
        <ToggleIcon className={`h-5 w-5 ${criterion.enabled ? "text-[var(--color-primary)]" : "text-[var(--color-muted-foreground)]"}`} />
      </button>

      {/* Remove */}
      <button onClick={onRemove} className="flex-shrink-0 text-[var(--color-muted-foreground)] hover:text-[var(--color-destructive)]" aria-label="Remove criterion">
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

function ThresholdSlider({ label, value, onChange, color, max }: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  color: string;
  max: number;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-[var(--color-muted-foreground)]">{label}</span>
        <span className="text-xs font-bold text-[var(--color-foreground)]">{value}</span>
      </div>
      <input
        type="range"
        min={0}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full"
        style={{ accentColor: color }}
      />
    </div>
  );
}

function ScoringSettingsSkeleton() {
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <Skeleton className="h-8 w-48" />
      <SkeletonCard className="h-24" />
      {[1, 2, 3, 4].map((i) => (
        <SkeletonCard key={i} className="h-16" />
      ))}
    </div>
  );
}
